<div class="header" >
  <div class="content">
    <div class="contentInner">
      <div class="pageTitle" appReveal>
        <h1 class="headline">Privacy policy</h1>
      </div>
    </div>
  </div>
</div>
<div class="content">
  <div class="contentInner">
    <h2 class="sectionHeader">Dash privacy policy</h2>
    <p class="intro">This privacy statement discloses the privacy practices of Apt Software Ltd. and all websites and
      mobile apps owned or operated by Apt Software Ltd. and its subsidiaries and corporate affiliates.</p>

    <h3>What Information We Collect</h3>
    <p>The types of information that we collect fall under two general categories: personally identifiable information (PII)
      and non-personally identifiable information (non-PII). PII consists of any information which can be used to specifically
      identify you as an individual or organization, whereas non-PII consists of aggregate information or any information that does not reveal
      your identity. The following sections describe how your PII and non-PII are collected by us, and how we use such information.
    </p>

    <h3>How We Collect and Use Information</h3>

    <p><b>Customer Data - </b>Customers or individuals granted access to our web dashboard routinely submit Customer Data
      (such as the configuration preferences for their Dash display devices, which may include the names of meeting rooms
      and company branding colors and images).  This data is only used in order to provide customers with the requested
      service.  This information is only collected when entered into our web dashboard, and is not collected or stored
      for customers using Dash as a standalone app or configuring Dash devices via a third-party MDM service.

    <p><b>Log Files - </b>Any time you visit any of our websites, our servers automatically gather information from your
      browser (such as your IP addresses, browser type, Internet service provider (ISP), referring/exit pages, platform type,
      date/time stamp, and number of clicks) to analyze trends, administer the site, prevent fraud, track visitor movement
      in the aggregate, and gather broad demographic information. For example, we may log your IP address for system
      administration purposes. IP addresses are logged to track a user’s session. This gives us an idea of which parts of
      our site users are visiting. We do not share the log files externally.</p>

    <p><b>Contact Us form - </b>When submitting a contact request via our <a routerLink="/contact">Contact Us</a> form, you are
      required to enter a name and email address.  Your name and email address will be stored by us for the purpose of
      fulfilling your contact request.</p>

    <p><b>Cookies - </b>We may use “cookies” to keep track of some types of information while you are visiting our website or
      using our services. Cookies are very small files placed on your computer, and they allow us to count the number of
      visitors to our website and distinguish repeat visitors from new visitors. They also allow us to save user preferences
      and track user trends. We may rely on cookies for the proper operation of our website; therefore if your browser is set
      to reject all cookies, the website may not function properly. Users who refuse cookies assume all responsibility
      for any resulting loss of functionality. We do not link the cookies to any PII.
    </p>

    <p><b>Mobile Analytics - </b>We use mobile analytics software to allow us to better understand the functionality of
      our mobile software on your device. This software may record information such as how often you use the application,
      the events that occur within the application, aggregated usage, performance data, and where the application was
      downloaded from. We do not link the information we store within the analytics software to any PII.  Sensitive data
      such as attendee information, calendar names, and meeting names and descriptions will never be recorded.
    </p>

    <p><b>Mobile Device - </b>If you use a mobile device to access our site or download any of our applications, we may
      collect device information (such as your mobile device ID, model and manufacturer), operating system and version
      information, and IP address.
    </p>

    <p><b>Third-Party Analytics - </b>We use a tool called “Plausible Analytics” to collect information about
      the use of our Website Services (e.g., Plausible Analytics collects information such as how often users visit the
      Website Services, what pages they visit when they do so, and what other sites they used prior to coming to the
      Website Services). Plausible Analytics is a privacy-first analytics tool that collects only the IP address assigned
      to you on the date that you visit the Website Services, rather than your name or other identifying information.
      The information collected through the use of Plausible Analytics is not combined with your Personal Information.
      You can learn more about how Plausible Analytics collects and processes data at
      <a href="https://plausible.io/data-policy">https://plausible.io/data-policy</a>. We
      also may use other third-party analytics tools to collect similar information about use of certain online Services.
    </p>

    <p><b>Meeting Analytics - </b>Dash Enterprise customers may opt to collect meeting analytics from their Dash display
      devices, which is displayed in the web management dashboard.  The information transmitted by the Dash devices when
      meeting analytics is enabled consists of:
    </p>
    <p>For each meeting room:</p>
    <ul>
      <li>The name of the meeting room</li>
      <li>The id of the meeting room (either the email address of the room, or a random id, depending on the calendar service you use)</li>
      <li>The capacity of the room (if present)</li>
    </ul>
    <p>For each meeting event:</p>
    <ul>
      <li>The start and end time of the event</li>
      <li>The number of attendees (no details about the names or email addresses of the attendees)</li>
      <li>Whether or not the meeting has been checked in</li>
      <li>The timing of any events that occur while the meeting is in progress (which includes extending the meeting,
        ending it early, and when your Dash display removes an abandoned meeting from the room calendar)</li>
    </ul>
    <p>Meeting analytics is disabled by default, and must be enabled on a particular device or group of devices to transmit information.  If you do not wish
      to use analytics, it is possible to permanently disable analytics for your entire organization by contacting us
      and requesting that the feature be disabled.</p>

    <h3>Disclosure of Your Information</h3>
    <p><b>Disclosure By Law - </b>You acknowledge and agree that we may disclose information you provide if required to do
      so by law, at the request of a third party, or if we, in our sole discretion, believe that disclosure is reasonable
      to (1) comply with the law, requests or orders from law enforcement, or any legal process (whether or not such
      disclosure is required by applicable law); or (2) protect or defend Apt Software’s, or a third party’s, rights or
      property.</p>

    <p><b>Disclosure to Trusted Third Parties By Us - </b>We may share your non-PII with third parties, but not in a
      manner that would reveal your identity. We may also share your PII, sometimes in conjunction with your non-PII,
      with service providers that may be associated with us to perform functions on our behalf. For example, outsourced
      customer care agents or technology assistants may need access to your information to perform services for you.
      Your information will be treated as private and confidential by such service providers and not used for any other
      purpose than we authorize.
    </p>

    <p><b>Information Transferred As a Result of Sale of Business - </b>As we develop our business, we may buy or sell
      assets and, depending on the transaction, your PII may be one of the transferred assets. In the event that we are
      acquired by another company, your PII may be part of the assets transferred to the acquiring party. If this occurs,
      you will be notified if there are any material changes to the way your PII is collected or used.
    </p>

    <h3>Security</h3>

    <p>We have extensive security measures in place to protect the loss, misuse and alteration of the information stored
      by us or transmitted to us. These measures include the use of Secure Socket Layer (SSL) and/or strong encryption
      technology, as well as other proprietary security measures which are applied to all repositories and transfers of
      user information. We will exercise reasonable care in providing secure transmission of information between your
      computer and our servers, but given that no information transmitted over the Internet can be guaranteed 100% secure,
      we cannot ensure or warrant the security of any information transmitted to us over the Internet and hence accept
      no liability for any unintentional disclosure.
    </p>

    <h3>Data Retention</h3>
    <p>We retain your information for continued service and convenience purposes until you advise us not to do so.
      Notwithstanding the foregoing, we may retain your information in order to comply with applicable laws, keep accurate
      records, resolve disputes, prevent fraud, enforce our Terms and Conditions of Service or other agreements, or for
      any other legally permissible purpose.
    </p>

    <h3>Acceptance of Privacy Statement</h3>

    <p>Your use of our website(s) and mobile apps, including any dispute concerning privacy, is subject to this privacy statement and the
      applicable <a routerLink="/terms-of-service">Terms and Conditions of Service</a>. <b>BY USING OUR WEBSITES AND MOBILE APPS, YOU ARE ACCEPTING THE
        PRACTICES SET OUT IN THIS PRIVACY STATEMENT AND THE APPLICABLE TERMS AND CONDITIONS OF SERVICE.</b></p>

    <p>If we decide to change our privacy policy, we will post those changes to this privacy statement page and any other
      places we deem appropriate so that you are aware of what information we collect, how we use it, and under what
      circumstances, if any, we disclose it. We reserve the right to modify this privacy statement at any time, so please
      review it frequently. If we make material changes to this policy, we will notify you here, or by other means,
      such as e-mail, at our discretion.</p>

    <p>Your continued use of any portion of our websites or mobile apps following posting of the updated privacy statement will constitute
      your acceptance of the changes.</p>

    <h3>Effective Date</h3>
    <p>This privacy statement was last revised on June 20, 2025.</p>

    <p>Copyright © 2019-2026 Apt Software Ltd. All Rights Reserved.</p>

  </div>
</div>
